import { CompProps, CompChildren } from '@fable/types'
import { css, cx } from '@fable/theme'
import { ScreenshotLoader } from './ScreenshotLoader'
import { useLoadScreenshot } from './useLoadScreenshot'
import { ScreenshotError } from './ScreenshotError'
import { useRef } from 'react'
import { useScreenshot } from './lib/ctx'
import { useImageLoaded } from './lib/hooks'

interface Props {
  width?: number
  altTag?: string
  imgStyles?: string
  retry?: number
  delay?: number
  footer?: CompChildren
  googleFonts?: string[]
  hideLoader?: boolean
}

export const Screenshot = ({
  width = 392,
  altTag = 'screenshot',
  children,
  imgStyles = '',
  retry = 1,
  delay = 500,
  footer,
  googleFonts = [],
  hideLoader = false,
}: CompProps<Props>) => {
  const componentRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<HTMLImageElement>(null)
  const [status, svg, image] = useScreenshot((s) => [s.status, s.svg, s.image])
  const svgLoaded = useImageLoaded(svg)

  useLoadScreenshot({ componentRef, width, retry, delay, googleFonts })

  const getImage = () => {
    if (status === 'loading') return hideLoader ? null : <ScreenshotLoader />
    if (status === 'error') return <ScreenshotError />

    return (
      <>
        <img
          data-testid="screenshot"
          className={cx(
            css`
              display: block;
              width: 100%;
            `,
            imgStyles
          )}
          src={image}
          alt={altTag}
        />
        {footer || null}
      </>
    )
  }

  return (
    <>
      {!!image.length ? null : (
        <div
          className={css`
            position: fixed;
            top: 0;
            right: 0;
            z-index: -99999;
            opacity: 0;
            pointer-events: none;
          `}
        >
          <div ref={componentRef}>{children}</div>
          {!!svg.length && svgLoaded ? (
            <img
              ref={svgRef}
              className={css`
                display: block;
                width: 100%;
              `}
              src={svg}
              alt={altTag}
            />
          ) : null}
        </div>
      )}
      {getImage()}
    </>
  )
}
